import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Thermometer, 
  ThermometerSnowflake, 
  ThermometerSun, 
  MinusCircle, 
  PlusCircle, 
  Upload, 
  ArrowLeft, 
  Save, 
  Trash2,
  Map,
  AlertTriangle,
  CheckCircle,
  WifiOff,
  Wifi,
  RotateCcw,
  ZoomIn,
  ZoomOut,
  Move,
  MapPin,
  Layers
} from 'lucide-react';
import { API_CONFIG } from '../config/api.config';
import { useGetLiveDataQuery, useGetZoneSensorsQuery } from '../services/sensorApi';
import { useGetPlanQuery, useUploadPlanMutation, useDeletePlanMutation } from '../services/planApi';
import { useGetZonesQuery } from '../services/zoneApi';
import { useSelector } from 'react-redux';
import type { RootState } from '../store/store';
import { FaTemperatureHigh } from 'react-icons/fa';
import { useUploadSitePlanMutation, useGetSitesQuery } from '../services/siteApi';

interface SavedPlanState {
  imageData: string;
  scale: number;
  sensorPositions: SensorPosition[];
  timestamp: number;
  zoneId: number;
}

interface SensorData {
  sensor_id: number;
  sensor_name: string;
  type: string;
  temperature: number;
  humidity: number | null;
  RSSI: string | null;
  SNR: string | null;
  Battery: string;
  timestamp: string;
}

interface SensorPosition {
  sensorId: number;
  x: number;
  y: number;
  size: number;
}

interface SitePlanData {
  siteId: number;
  siteDescription: string;
  imageBuffer: string;
  scale: number;
  sensorPositions: Array<{
    sensorId: number;
    x: number;
    y: number;
  }>;
  siteUsers: number[];
  siteImage: string;
}

// Add this type for the API response
interface SitePlanResponse {
  status: string;
  data: Array<{
    siteId: number;
    siteDescription: string;
    siteplan: {
      type: string;
      data: number[];
    };
    siteUsers: string;
    siteAddress: string | null;
  }>;
}

interface RenderState {
  hasPlanImage: boolean;
  planImageUrl: string | null;
  scale: number;
  sensorPositionsCount: number;
  isLoading: boolean;
}

// --- Bolt-style SitePlanSensor ---
interface SitePlanSensorProps {
  sensor: {
    id: string;
    name: string;
    temp: number;
    humidity: number;
    status: string;
    x: number;
    y: number;
  };
  onDrag: (id: string, x: number, y: number) => void;
  onDelete: (id: string) => void;
  isSelected: boolean;
  onSelect: (id: string) => void;
}

const SitePlanSensor: React.FC<SitePlanSensorProps> = ({
  sensor,
  onDrag,
  onDelete,
  isSelected,
  onSelect
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online': return 'bg-green-500 border-green-400';
      case 'warning': return 'bg-yellow-500 border-yellow-400';
      case 'offline': return 'bg-red-500 border-red-400';
      default: return 'bg-gray-500 border-gray-400';
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStart({
      x: e.clientX - sensor.x,
      y: e.clientY - sensor.y
    });
    onSelect(sensor.id);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging) {
      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;
      onDrag(sensor.id, Math.max(0, newX), Math.max(0, newY));
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragStart]);

  return (
    <div
      className={`absolute cursor-move select-none transition-all duration-200 ${isSelected ? 'z-20' : 'z-10'}`}
      style={{ left: sensor.x, top: sensor.y }}
      onMouseDown={handleMouseDown}
    >
      <div
        className={`w-8 h-8 rounded-full border-2 flex items-center justify-center ${getStatusColor(sensor.status)} ${isSelected ? 'ring-2 ring-blue-400 ring-offset-2 ring-offset-gray-800' : ''} ${isDragging ? 'scale-110 shadow-lg' : 'hover:scale-105'} transition-all duration-200`}
      >
        <Thermometer className="w-4 h-4 text-white" />
      </div>
      <div className={`absolute top-10 left-1/2 transform -translate-x-1/2 bg-gray-800 border border-gray-600 rounded-lg p-2 min-w-max shadow-lg ${isSelected || isDragging ? 'opacity-100' : 'opacity-0 hover:opacity-100'} transition-opacity duration-200`}>
        <div className="text-xs text-white font-medium">{sensor.name}</div>
        <div className="text-xs text-gray-300">{sensor.temp}°C • {sensor.humidity}%</div>
        <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-800 border-l border-t border-gray-600 rotate-45"></div>
      </div>
      {isSelected && (
        <button
          onClick={e => { e.stopPropagation(); onDelete(sensor.id); }}
          className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center transition-colors duration-200"
        >
          <Trash2 className="w-3 h-3 text-white" />
        </button>
      )}
    </div>
  );
};

// --- Bolt-style SitePlanEditor ---
const SitePlanEditor: React.FC<{
  sensors: any[];
  onUpdateSensors: (sensors: any[]) => void;
  planImage: string | null;
  onUploadPlan: (file: File) => void;
  onSave: (sensors: any[], planImage: string | null) => void;
}> = ({ sensors, onUpdateSensors, planImage, onUploadPlan, onSave }) => {
  const [selectedSensor, setSelectedSensor] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      onUploadPlan(file);
    }
  };

  const handleSensorDrag = (id: string, x: number, y: number) => {
    const updatedSensors = sensors.map(sensor =>
      sensor.id === id ? { ...sensor, x, y } : sensor
    );
    onUpdateSensors(updatedSensors);
  };

  const handleSensorDelete = (id: string) => {
    const updatedSensors = sensors.filter(sensor => sensor.id !== id);
    onUpdateSensors(updatedSensors);
    setSelectedSensor(null);
  };

  const handleCanvasMouseDown = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setSelectedSensor(null);
      setIsPanning(true);
      setPanStart({
        x: e.clientX - pan.x,
        y: e.clientY - pan.y
      });
    }
  };

  const handleCanvasMouseMove = (e: MouseEvent) => {
    if (isPanning) {
      setPan({
        x: e.clientX - panStart.x,
        y: e.clientY - panStart.y
      });
    }
  };

  const handleCanvasMouseUp = () => {
    setIsPanning(false);
  };

  useEffect(() => {
    if (isPanning) {
      document.addEventListener('mousemove', handleCanvasMouseMove);
      document.addEventListener('mouseup', handleCanvasMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleCanvasMouseMove);
        document.removeEventListener('mouseup', handleCanvasMouseUp);
      };
    }
  }, [isPanning, panStart]);

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.1, 2));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.1, 0.5));
  const handleResetView = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  const handleSave = () => {
    onSave(sensors, planImage);
  };

  return (
    <div className="space-y-6">
      {/* Site Plan Controls */}
      <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold text-lg flex items-center gap-2">
            <Map className="w-5 h-5" />
            Site Plan Editor
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={handleZoomOut}
              className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
              title="Zoom Out"
            >
              <ZoomOut className="w-4 h-4 text-white" />
            </button>
            <span className="text-white text-sm px-2">{Math.round(zoom * 100)}%</span>
            <button
              onClick={handleZoomIn}
              className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
              title="Zoom In"
            >
              <ZoomIn className="w-4 h-4 text-white" />
            </button>
            <button
              onClick={handleResetView}
              className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
              title="Reset View"
            >
              <RotateCcw className="w-4 h-4 text-white" />
            </button>
            <button
              onClick={handleSave}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
            >
              <Save className="w-4 h-4" />
              Save Layout
            </button>
          </div>
        </div>
        {/* Upload Section */}
        {!planImage && (
          <div className="border-2 border-dashed border-gray-600 rounded-lg p-8 text-center">
            <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h4 className="text-white font-medium mb-2">Upload Site Plan</h4>
            <p className="text-gray-400 mb-4">Upload a floor plan or site map to position your sensors</p>
            <label className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg cursor-pointer transition-colors">
              <Upload className="w-4 h-4" />
              Choose File
              <input
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                className="hidden"
              />
            </label>
          </div>
        )}
        {/* Site Plan Canvas */}
        {planImage && (
          <div className="relative bg-gray-900 rounded-lg overflow-hidden border border-gray-600">
            <div
              className="relative w-full h-96 cursor-move"
              onMouseDown={handleCanvasMouseDown}
              style={{
                transform: `scale(${zoom}) translate(${pan.x}px, ${pan.y}px)`,
                transformOrigin: 'center center'
              }}
            >
              <img
                src={planImage}
                alt="Site Plan"
                className="w-full h-full object-contain pointer-events-none"
                draggable={false}
              />
              {/* Sensors */}
              {sensors.map((sensor) => (
                <SitePlanSensor
                  key={sensor.id}
                  sensor={sensor}
                  onDrag={handleSensorDrag}
                  onDelete={handleSensorDelete}
                  isSelected={selectedSensor === sensor.id}
                  onSelect={setSelectedSensor}
                />
              ))}
            </div>
            {/* Grid Overlay */}
            <div className="absolute inset-0 pointer-events-none opacity-20">
              <div className="w-full h-full" style={{
                backgroundImage: `
                  linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                  linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
                `,
                backgroundSize: '20px 20px'
              }}></div>
            </div>
            {/* Instructions */}
            <div className="absolute top-4 left-4 bg-gray-800/90 backdrop-blur-sm rounded-lg p-3 border border-gray-600">
              <div className="text-xs text-gray-300 space-y-1">
                <div className="flex items-center gap-2">
                  <Move className="w-3 h-3" />
                  <span>Drag sensors to reposition</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-3 h-3" />
                  <span>Click sensor to select</span>
                </div>
                <div className="flex items-center gap-2">
                  <Trash2 className="w-3 h-3" />
                  <span>Delete button appears when selected</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Sensor Legend */}
      <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
        <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
          <Layers className="w-5 h-5" />
          Sensor Legend
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center gap-3">
            <div className="w-4 h-4 rounded-full bg-green-500 border border-green-400"></div>
            <span className="text-gray-300">Online Sensors</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-4 h-4 rounded-full bg-yellow-500 border border-yellow-400"></div>
            <span className="text-gray-300">Warning Status</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-4 h-4 rounded-full bg-red-500 border border-red-400"></div>
            <span className="text-gray-300">Offline Sensors</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const API_BASE = 'https://s3.ts1cloud.com/service3';

const getHeaders = (user: any) => ({
  'Content-Type': 'application/json',
  'x-company-id': user?.companyID?.toString() || '',
  'x-company-name': user?.companyName || '',
});

export default function PlanViewPage() {
  const user = useSelector((state: RootState) => state.auth.user);
  const [sites, setSites] = useState<any[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);
  const [planImage, setPlanImage] = useState<string | null>(null);
  const [sensors, setSensors] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // Fetch all sites on mount
  useEffect(() => {
    if (!user) return;
    setLoading(true);
    fetch(`${API_BASE}/sites`, { headers: getHeaders(user) })
      .then(res => res.json())
      .then(data => {
        setSites(data.data || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [user]);

  // Fetch site plan and sensors when site changes
  useEffect(() => {
    if (!user || !selectedSiteId) return;
    setLoading(true);
    fetch(`${API_BASE}/sites/${selectedSiteId}/plan`, { headers: getHeaders(user) })
      .then(res => res.json())
      .then(data => {
        if (data && data.imageBuffer) {
          setPlanImage(`data:image/png;base64,${data.imageBuffer}`);
        } else {
          setPlanImage(null);
        }
        // Map sensor positions to Bolt format
        const positions = data.sensorPositions || [];
        const mapped = positions.map((pos: any, idx: number) => ({
          id: String(pos.sensorId),
          name: pos.sensorName || `Sensor ${pos.sensorId}`,
          temp: pos.temp ?? 0,
          humidity: pos.humidity ?? 0,
          status: pos.status ?? 'online',
          x: pos.x,
          y: pos.y
        }));
        setSensors(mapped);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [user, selectedSiteId]);

  // Upload or update plan
  const handleUploadPlan = async (file: File) => {
    if (!user) return;
    const reader = new FileReader();
    reader.onload = async (e) => {
      const base64 = (e.target?.result as string).split(',')[1];
      setLoading(true);
      // If there are no sites, omit siteId for first upload
      const isFirstSite = sites.length === 0 || !selectedSiteId;
      const body: any = {
        siteDescription: isFirstSite ? 'New Site' : (sites.find((s: any) => s.siteId === selectedSiteId)?.siteDescription || ''),
        sitePlan: base64,
        scale: 100,
        sensorPositions: sensors.map((s: any) => ({ sensorId: Number(s.id), x: s.x, y: s.y }))
      };
      if (isFirstSite) {
        body.siteUsers = user?.id ? [user.id] : [];
        body.siteAddress = '';
        body.is_deleted = false;
      }
      if (!isFirstSite) body.siteId = selectedSiteId;
      console.log('Uploading site plan with body:', body);
      const response = await fetch(`${API_BASE}/sites`, {
        method: 'POST',
        headers: getHeaders(user),
        body: JSON.stringify(body)
      });
      const result = await response.json();
      console.log('Upload response:', result);
      if (result.status !== 'success') {
        alert('Upload failed: ' + (result.message || JSON.stringify(result)));
        setLoading(false);
        return;
      }
      setPlanImage(`data:image/png;base64,${base64}`);
      // Refetch plan to get latest positions
      if (!isFirstSite) {
        fetch(`${API_BASE}/sites/${selectedSiteId}/plan`, { headers: getHeaders(user) })
          .then(res => res.json())
          .then(data => {
            setPlanImage(data && data.imageBuffer ? `data:image/png;base64,${data.imageBuffer}` : null);
            setLoading(false);
          });
      } else {
        // Refetch all sites and select the new one
        fetch(`${API_BASE}/sites`, { headers: getHeaders(user) })
          .then(res => res.json())
          .then(data => {
            setSites(data.data || []);
            if (data.data && data.data.length > 0) {
              const newSiteId = data.data[data.data.length - 1].siteId;
              setSelectedSiteId(newSiteId);
            }
            setLoading(false);
          });
      }
    };
    reader.readAsDataURL(file);
  };

  // Save sensor positions
  const handleSave = async (updatedSensors: any[], planImageStr: string | null) => {
    if (!user || !selectedSiteId) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/sites/${selectedSiteId}/plan`, {
        method: 'PATCH',
        headers: getHeaders(user),
        body: JSON.stringify({
          sensorPositions: updatedSensors.map((s: any) => ({ sensorId: Number(s.id), x: s.x, y: s.y }))
        })
      });
      const result = await response.json();
      if (result.status !== 'success') {
        alert('Save failed: ' + (result.message || JSON.stringify(result)));
        setLoading(false);
        return;
      }
      // Refetch plan to get latest positions
      fetch(`${API_BASE}/sites/${selectedSiteId}/plan`, { headers: getHeaders(user) })
        .then(res => res.json())
        .then(data => {
          setPlanImage(data && data.imageBuffer ? `data:image/png;base64,${data.imageBuffer}` : null);
          setLoading(false);
          alert('Layout saved successfully!');
        });
    } catch (err) {
      alert('Save failed: ' + err);
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <Link 
          to="/" 
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Dashboard
        </Link>
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Plan View</h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              View and manage your site layouts
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <select
              value={selectedSiteId || ''}
              onChange={e => setSelectedSiteId(e.target.value ? Number(e.target.value) : null)}
              className="block w-48 pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="">Select Site</option>
              {sites.map((site) => (
                <option key={site.siteId} value={site.siteId}>
                  {site.siteDescription}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {loading && <div className="text-gray-500">Loading...</div>}

      <SitePlanEditor
        sensors={sensors}
        onUpdateSensors={setSensors}
        planImage={planImage}
        onUploadPlan={handleUploadPlan}
        onSave={handleSave}
      />
    </div>
  );
} 